import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Copy, Link2Off, ExternalLink } from "lucide-react";
import { NudgeBanner } from "@/components/NudgeBanner";

export function PartnerShareCard() {
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/share/partner").then(({ data }) => setShare(data)).catch(() => setShare({ active: false }));
  }, []);

  const url = share?.token ? `${window.location.origin}/share/${share.token}` : "";

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/share/partner");
      setShare(data);
      toast.success("Your partner link is ready.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete("/share/partner");
      setShare({ active: false });
      toast.success("Link turned off. The page now shows nothing.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied.");
    } catch {
      toast.error("Couldn't copy — select the link and copy it by hand.");
    }
  };

  return (
    <div className="mt-6 card-paper p-6 sm:p-8" data-testid="settings-partner-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-[#F4EDE2] flex items-center justify-center text-[#2C221E]">
          <Users size={18} />
        </div>
        <div>
          <h2 className="font-serif-display text-xl text-[#2C221E]">Partner view</h2>
          <p className="text-sm text-[#73635B] mt-1 max-w-md">
            A read-only glance at your last seven days — sleep, energy, mood words, hot flashes, and one line on how to help.
            Your notes and companion conversations are never included. Turn it off any time.
          </p>
        </div>
      </div>

      {share === null ? (
        <p className="mt-6 text-sm text-[#8C7A70]">one moment…</p>
      ) : share.active ? (
        <div className="mt-6">
          <div className="flex items-center gap-2 flex-wrap">
            <input className="input-paper flex-1 min-w-[220px] text-sm" readOnly value={url} data-testid="settings-partner-link" onFocus={(e) => e.target.select()} />
            <button className="btn-ghost inline-flex items-center gap-2" onClick={copy} data-testid="settings-partner-copy">
              <Copy size={14} /> Copy
            </button>
            <a className="btn-ghost inline-flex items-center gap-2" href={url} target="_blank" rel="noreferrer" data-testid="settings-partner-open">
              <ExternalLink size={14} /> Open
            </a>
          </div>
          <button className="mt-4 text-sm text-[#C26D53] inline-flex items-center gap-2 hover:underline" onClick={revoke} disabled={busy} data-testid="settings-partner-revoke">
            <Link2Off size={14} /> Turn off this link
          </button>
          <NudgeBanner compact />
        </div>
      ) : (
        <div className="mt-6">
          <button className="btn-primary" onClick={create} disabled={busy} data-testid="settings-partner-create">
            {busy ? "One moment…" : "Create a partner link"}
          </button>
        </div>
      )}
    </div>
  );
}
